/**
 * The printable weekly sheet.
 *
 * The paper format this family started with: one page per day with a table
 * of blocks (Block, Domain, Activity, Done, Notes) and blank room to write,
 * then the week's check-in table at the end. Rendered from the same rows the
 * screens use, so what prints is what is planned. Filled sheets come back in
 * through a photo; `read_sheet` on the agent side knows this exact layout.
 */

export interface SheetBlock {
  slot: number
  title: string
  instructions?: string
  minutes?: number
  domainName?: string
  status?: string
  note?: string
  materials?: string[]
}

export interface SheetDay {
  date: string
  caregiving?: string
  blocks: SheetBlock[]
}

export interface SheetInput {
  childName: string
  week?: { number?: number; theme?: string; focus?: string; startDate?: string; materialsList?: string[]; checkinQuestions?: string[] }
  monday: string
  days: SheetDay[]
  /** The domains to rate in the check-in, in order. */
  domains: Array<{ key: string; name: string }>
  /** Printed in the footer so a photographed page can be matched back. */
  generatedAt?: number
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function longDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return `${WEEKDAYS[(d.getUTCDay() + 6) % 7]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}
function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}
const esc = (s: unknown): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** The paper's own block names, by position in the day (rows are sorted by slot). */
const SLOT_LABELS = ['AM-1', 'AM-2', 'MID', 'PM-1', 'PM-2', 'PM-3', 'EVE']

/** A stable name for the file: "Aurora-week-3-2026-09-14". */
export function sheetFileName(input: SheetInput): string {
  const who = input.childName.replace(/[^\w]+/g, '-')
  return `${who}-week-${input.week?.number ?? ''}-${input.monday}`.replace(/-+/g, '-').replace(/-$/, '')
}

const DONE_KEY = '<span class="key">Done: <b>✓</b> did it &nbsp; <b>◐</b> partly &nbsp; <b>✗</b> skipped</span>'

export function renderWeekSheet(input: SheetInput): string {
  const { childName, week, monday, days, domains } = input
  const title = `${childName}'s week${week?.number ? ` ${week.number}` : ''}`
  const sub = [week?.theme, `Week of ${shortDate(monday)}`].filter(Boolean).join(' · ')
  const generated = input.generatedAt ? new Date(input.generatedAt).toISOString().slice(0, 10) : undefined

  const materials = (week?.materialsList ?? []).filter(Boolean)
  const questions = (week?.checkinQuestions ?? []).filter(Boolean)

  const dayPages = days
    .filter((d) => d.blocks.length)
    .map((d) => {
      const rows = [...d.blocks].sort((a, b) => a.slot - b.slot).map((b, i) => `
        <tr>
          <td class="slot">${esc(SLOT_LABELS[Math.min(i, SLOT_LABELS.length - 1)] ?? `#${i + 1}`)}<br><span class="min">${b.minutes ?? 10} min</span></td>
          <td class="domain">${esc(b.domainName ?? '')}</td>
          <td class="activity"><b>${esc(b.title)}</b>${b.instructions ? `<div class="how">${esc(b.instructions)}</div>` : ''}${b.materials?.length ? `<div class="have">Have ready: ${esc(b.materials.join(', '))}</div>` : ''}</td>
          <td class="done"><span class="box">${b.status === 'done' ? '✓' : b.status === 'partial' ? '◐' : b.status === 'skipped' ? '✗' : ''}</span></td>
          <td class="notes">${b.note ? esc(b.note) : ''}</td>
        </tr>`).join('')
      return `
      <section class="day">
        <header class="dayhead">
          <h2>${esc(longDate(d.date))}</h2>
          <span class="key-line">${DONE_KEY}</span>
        </header>
        <table class="blocks">
          <thead><tr><th>Block</th><th>Domain</th><th>Activity</th><th>Done</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        ${d.caregiving ? `<p class="caregiving"><b>Woven in:</b> ${esc(d.caregiving)}</p>` : ''}
        <div class="dayline"><span>What she said or did today:</span><div class="rule"></div><div class="rule"></div></div>
      </section>`
    }).join('')

  const ratingRows = domains.map((d) => `
    <tr><td class="dname">${esc(d.name)}</td>
    <td class="rating"><span class="opt">−</span><span class="opt">=</span><span class="opt">+</span><span class="opt">++</span></td>
    <td class="obs"></td></tr>`).join('')
  const questionRows = questions.map((q) => `<tr><td class="q">${esc(q)}</td><td class="a"></td></tr>`).join('')

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title>
<meta name="sapling-sheet" content="week:${esc(monday)};child:${esc(childName)}">
<style>
  @page { size: letter; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; color: #1a1815; background: #fff; font: 11pt/1.35 Georgia, 'Iowan Old Style', 'Times New Roman', serif; }
  .cover { page-break-after: always; }
  h1 { font-size: 24pt; margin: 0 0 2pt; font-weight: 600; letter-spacing: -0.01em; }
  .sub { color: #555; margin: 0 0 14pt; font-size: 12pt; }
  .focus { border-left: 3px solid #2f7a4b; padding: 4pt 10pt; margin: 0 0 14pt; color: #333; }
  h2 { font-size: 15pt; margin: 0; font-weight: 600; }
  h3 { font-size: 11pt; margin: 14pt 0 4pt; text-transform: uppercase; letter-spacing: 0.06em; color: #666; font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; }
  .day { page-break-after: always; }
  .day:last-of-type { page-break-after: auto; }
  .dayhead { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 6pt; }
  .key-line { font-size: 8.5pt; color: #666; font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; }
  .key b { font-weight: 600; color: #1a1815; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font: 600 8.5pt/1.2 -apple-system, 'Helvetica Neue', Arial, sans-serif; text-transform: uppercase; letter-spacing: 0.06em; color: #666; padding: 4pt 5pt; border-bottom: 1.5px solid #1a1815; }
  td { vertical-align: top; padding: 6pt 5pt; border-bottom: 1px solid #cfc8bc; }
  .blocks td.slot { width: 12%; font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; font-weight: 600; font-size: 9.5pt; white-space: nowrap; }
  .blocks .min { font-weight: 400; color: #777; font-size: 8.5pt; }
  .blocks td.domain { width: 13%; font-size: 9.5pt; color: #444; }
  .blocks td.activity { width: 40%; }
  .blocks .how { font-size: 9.5pt; color: #333; margin-top: 2pt; }
  .blocks .have { font-size: 8.5pt; color: #666; margin-top: 3pt; font-style: italic; }
  .blocks td.done { width: 8%; text-align: center; }
  .box { display: inline-block; width: 18pt; height: 18pt; border: 1.5px solid #1a1815; border-radius: 3pt; line-height: 17pt; font-size: 12pt; text-align: center; }
  .blocks td.notes { width: 27%; min-height: 40pt; height: 44pt; }
  .caregiving { margin: 8pt 0 0; font-size: 10pt; color: #333; }
  .dayline { margin-top: 14pt; font-size: 9.5pt; color: #666; font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; }
  .rule { border-bottom: 1px solid #cfc8bc; height: 20pt; }
  .materials { columns: 2; column-gap: 20pt; padding-left: 14pt; margin: 0; font-size: 10pt; }
  .materials li { break-inside: avoid; margin-bottom: 2pt; }
  .checkin td.dname { width: 30%; font-weight: 600; }
  .checkin td.rating { width: 24%; white-space: nowrap; font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; }
  .checkin .opt { display: inline-block; width: 22pt; height: 18pt; line-height: 16pt; border: 1px solid #999; border-radius: 3pt; text-align: center; margin-right: 3pt; font-size: 10pt; }
  .checkin td.obs { height: 30pt; }
  .questions td.q { width: 55%; }
  .questions td.a { height: 26pt; }
  .free { margin-top: 10pt; }
  .free .rule { height: 22pt; }
  footer { position: fixed; bottom: 0; left: 0; right: 0; font: 8pt -apple-system, 'Helvetica Neue', Arial, sans-serif; color: #999; display: flex; justify-content: space-between; }
</style></head>
<body>
<section class="cover">
  <h1>${esc(title)}</h1>
  <p class="sub">${esc(sub)}</p>
  ${week?.focus ? `<p class="focus">${esc(week.focus)}</p>` : ''}
  <h3>How to use this</h3>
  <p>Each day is one page. Tick <b>Done</b> as you go (✓ did it, ◐ partly, ✗ skipped) and jot what worked, what she struggled with, what she said. If she resists, note it and move on. If she is on fire with something, let her run and write down what happened. On Sunday fill in the check-in on the last page, then photograph every page and send them to Sapling, which reads them into the record.</p>
  ${materials.length ? `<h3>Gather before the week</h3><ul class="materials">${materials.map((m) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
  <h3>The week at a glance</h3>
  <table class="glance"><tbody>
    ${days.map((d) => `<tr><td style="width:28%"><b>${esc(longDate(d.date))}</b></td><td>${d.blocks.length ? [...d.blocks].sort((a, b) => a.slot - b.slot).map((b) => esc(b.title)).join(' · ') : '<i>Day off</i>'}</td></tr>`).join('')}
  </tbody></table>
</section>

${dayPages}

<section class="checkin-page">
  <h2>Week ${week?.number ?? ''} check-in</h2>
  <p class="sub">Sunday evening. Rate each area for the week, then answer the week's own questions.</p>
  <table class="checkin">
    <thead><tr><th>Area</th><th>Slipped · Held · Grew · Leapt</th><th>Key observations</th></tr></thead>
    <tbody>${ratingRows}</tbody>
  </table>
  ${questions.length ? `<h3>This week's questions</h3><table class="questions"><tbody>${questionRows}</tbody></table>` : ''}
  <h3>Biggest win</h3><div class="free"><div class="rule"></div><div class="rule"></div></div>
  <h3>What to adjust next week</h3><div class="free"><div class="rule"></div><div class="rule"></div></div>
  <h3>Anything else</h3><div class="free"><div class="rule"></div><div class="rule"></div><div class="rule"></div></div>
</section>
<footer><span>Sapling · ${esc(childName)} · week of ${esc(monday)}</span><span>${generated ? `printed ${esc(generated)}` : ''}</span></footer>
</body></html>`
}
